import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import GoogleSignInButton from '../../../../GoogleSignInButton'

interface SignUpFormProps {
    form: any
    status: any
    handlers: any
    tHeader: any
    t: any
    onGoogleSignIn: (userData: {
        email: string
        firstName: string
        lastName: string
        fullName: string
        googleId: string
        picture?: string
    }) => void
}

const SignUpForm = ({ form, status, handlers, onGoogleSignIn, t, tHeader }: SignUpFormProps) => {
    const pathname = usePathname()
    const locale = pathname.split('/')[1] || 'en'
    const tTitles = useTranslations('common.titles')

    return (
        <Box>
            <GoogleSignInButton onSuccess={onGoogleSignIn} buttonText="Google" fullWidth />
            <Typography align="center" sx={{ mb: 1, color: 'grey.500', fontWeight: 500 }}>
                {t('or')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl sx={{ minWidth: 80 }}>
                    <InputLabel id="title-select-label">{tHeader('title')}</InputLabel>
                    <Select
                        labelId="title-select-label"
                        id="title-select"
                        value={form.title}
                        label={tHeader('title')}
                        onChange={e => handlers.handlers.setTitle(e.target.value)}
                    >
                        <MenuItem value="Mr">{tTitles('mr')}</MenuItem>
                        <MenuItem value="Ms">{tTitles('ms')}</MenuItem>
                        <MenuItem value="Mrs">{tTitles('mrs')}</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label={t('firstName')}
                    fullWidth
                    value={form.firstName}
                    onChange={handlers.handlers.changeFirstNameHandler}
                    error={!form.isValidFirstName}
                    helperText={!form.isValidFirstName ? t('invalidFirstName') : ''}
                />
                <TextField
                    label={t('lastName')}
                    fullWidth
                    value={form.lastName}
                    onChange={handlers.handlers.changeLastNameHandler}
                    error={!form.isValidLastName}
                    helperText={!form.isValidLastName ? t('invalidLastName') : ''}
                />
            </Box>
            <TextField
                fullWidth
                label={t('email')}
                type="email"
                variant="outlined"
                sx={{ mb: 2 }}
                autoComplete="email"
                value={form.email}
                onChange={handlers.handlers.changeEmailHandler}
                error={!form.isEmailValid}
                helperText={!form.isEmailValid ? t('invalidEmail') : ''}
            />
            <TextField
                fullWidth
                label={t('password')}
                type={form.passwordType ? 'password' : 'text'}
                variant="outlined"
                sx={{ mb: 2 }}
                autoComplete="new-password"
                value={form.password}
                onChange={handlers.handlers.changePasswordHandler}
                error={!form.isPasswordValid}
                helperText={!form.isPasswordValid ? t('invalidPassword') : ''}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={() => handlers.handlers.setPasswordType(!form.passwordType)}
                                edge="end"
                            >
                                {form.passwordType ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            <Typography variant="caption" sx={{ color: 'grey.600', mb: 2, display: 'block', lineHeight: 1.7 }}>
                {t('bySigningUp')}{' '}
                <Link
                    href={`/termsOfUse.${locale}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontSize: 12,
                        mx: 0.5,
                        color: 'primary.main',
                        fontWeight: 500,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {t('termsOfUse')}
                </Link>{' '}
                {tHeader('and')}{' '}
                <Link
                    href={`/privacyPolicy.${locale}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontSize: 12,
                        mx: 0.5,
                        color: 'primary.main',
                        fontWeight: 500,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {t('privacyPolicy')}
                </Link>
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={form.travelCheckedStatus}
                        onChange={e => handlers.handlers.setTravelCheckedStatus(e.target.checked)}
                    />
                }
                label={<Typography sx={{ fontSize: 14 }}>{t('sendMeTravelOffers')}</Typography>}
            />
            <Button
                variant="contained"
                sx={{ width: '100%', mt: 2, fontWeight: 600, fontSize: 16 }}
                onClick={handlers.onSignUp}
                disabled={status.loading}
            >
                {status.loading ? <CircularProgress size={24} color="inherit" /> : t('signUp')}
            </Button>
        </Box>
    )
}

export default SignUpForm
